import React, { useState, useEffect, useRef } from 'react'
import PropTypes from 'prop-types'

import useQuery from '@/hooks/useQuery'
import { useForm } from 'react-hook-form'
import { useGlobalState } from '@/state'
import { randomToastSuccess } from '@/shared/Toast'
import { ExclamationCircleIcon } from '@heroicons/react/solid'
import Card from '@/shared/Card'
import PrimaryButton from '@/shared/Buttons/Primary'

const SubmissionUrl = ({ project, submission, selectedRole }) => {
  const [, setToast] = useGlobalState('toast')
  const { putpostRequest } = useQuery()
  const { formState, register, handleSubmit, setError, setFocus } = useForm({
   defaultValues: {
      remote_audition_link: submission?.remoteLink
    }
  })
  const { isDirty, errors } = formState
  const [loading, setLoading] = useState(false)
  const [captchaToken, setCaptchaToken] = useState(null)
  const turnstileRef = useRef(null)
  const widgetIdRef = useRef(null)

  const onSubmit = (data) => {
    // Only require captcha for new submissions, not edits
    if (!submission?.id && !captchaToken) {
      setToast(
        <div className="ml-3 w-0 flex-1 pt-0.5">
          <p className="text-sm font-medium text-red-600 dark:text-red-400">Verification Required</p>
          <p className="mt-1 text-sm text-red-500 dark:text-red-300">Please complete the human verification</p>
        </div>
      )
      return
    }

    setLoading(true)

    const formData = {
      ...data,
      role_id: selectedRole.id
    }

    // Only include turnstile token for new submissions
    if (!submission?.id) {
      formData['cf-turnstile-response'] = captchaToken
    }
    let url = '/api/v3/auditions'
    let action = 'POST'
    if (submission?.id) {
      url = `/api/v3/auditions/${submission.id}`
      action = 'PATCH'
    }

    putpostRequest(url, action, { audition: formData }, (err, jsonData) => {
      setLoading(false)
      if (err) { // 422 code
        if (typeof err !== 'string') { // 500 code on API
          Object.entries(err).forEach(([key, value]) => {
            setError(key, value)
          })
        }

        if (typeof err === 'string') {
          setToast(
            <div className="ml-3 w-0 flex-1 pt-0.5">
              <p className="text-sm font-medium text-red-600 dark:text-red-400">Air Or</p>
              <p className="mt-1 text-sm text-red-500 dark:text-red-300">{err}</p>
            </div>
          )
        }
        // Reset turnstile on error (only for new submissions)
        if (!submission?.id && window.turnstile && widgetIdRef.current !== null) {
          window.turnstile.reset(widgetIdRef.current)
          setCaptchaToken(null)
        }
        return
      }

      if (submission?.id) {
        setToast(randomToastSuccess())
      } else {
        window.location.href = `/submissions/${jsonData.id}/edit?success=true`
      }
    })
  }

  useEffect(() => {
    setFocus('remote_audition_link')
  }, [setFocus])

  useEffect(() => {
    // Only load Turnstile for new submissions, not edits
    if (submission?.id) return

    const cloudflaresitekey = (document.head.querySelector('[name~=cloudflareturnstile]') || {}).content
    // Load Turnstile script
    const script = document.createElement('script')
    script.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js'
    script.async = true
    script.defer = true
    script.onload = () => {
      // Initialize Turnstile widget
      if (window.turnstile && turnstileRef.current && widgetIdRef.current === null) {
        widgetIdRef.current = window.turnstile.render(turnstileRef.current, {
          sitekey: cloudflaresitekey,
          callback: (token) => {
            setCaptchaToken(token)
          },
          'error-callback': () => {
            setCaptchaToken(null)
            setToast(
              <div className="ml-3 w-0 flex-1 pt-0.5">
                <p className="text-sm font-medium text-red-600 dark:text-red-400">Verification Failed</p>
                <p className="mt-1 text-sm text-red-500 dark:text-red-300">Please try again</p>
              </div>
            )
          },
          'expired-callback': () => {
            setCaptchaToken(null)
          },
          theme: document.documentElement.classList.contains('dark') ? 'dark' : 'light'
        })
      }
    }
    document.head.appendChild(script)

    return () => {
      // Cleanup: remove widget when component unmounts
      if (window.turnstile && widgetIdRef.current !== null) {
        window.turnstile.remove(widgetIdRef.current)
        widgetIdRef.current = null
      }
    }
  }, [setToast, submission?.id])

  return <div className='max-w-xl mx-auto mt-6'>
    <Card title="Link Submission"
      footer={
        <div className='flex flex-col gap-y-4 py-2'>
          {!submission?.id && (
            <div className='flex items-center justify-center'>
              <div ref={turnstileRef}></div>
            </div>
          )}
          <div className='flex flex-rows justify-end items-center gap-x-2'>
            <PrimaryButton
              onClick={handleSubmit(onSubmit)}
              loading={loading}
              disabled={!submission?.id && !captchaToken}
              text='Save'
            />
          </div>
        </div>
      } >
      <div className="grid sm:grid-cols-4 gap-6">
        <div className="col-span-4 sm:col-span-4">
          <div className='mt-4'>
            <label htmlFor="youtube_url" className="block text-sm font-medium text-gray-700 dark:text-gray-300 flex justify-between">Link URL </label>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">Make sure you include the <b>https://</b> link. e.g. (https://www.youtube.com/embed/DiYTaQ-Mgck)
            </p>
            <div className="mt-1 flex flex-col rounded-md shadow-sm">
              <div className='relative'>
                <input type="text" className={errors.remote_audition_link ? 'errors' : ''} {...register('remote_audition_link', { required: true }) } placeholder="https://www.loom.com/embed/...." />
                { errors.remote_audition_link && <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                  <ExclamationCircleIcon className="h-5 w-5 text-red-500" aria-hidden="true" />
                </div> }
              </div>
            </div>
          </div>
        </div>
      </div>
    </Card>
  </div>
}

export default SubmissionUrl

SubmissionUrl.propTypes = {
  project: PropTypes.object.isRequired,
  selectedRole: PropTypes.object.isRequired,
  submission: PropTypes.object
}
